/**
 * stores/database.ts
 *
 * Database state management store
 * Manages per-user Dexie database instances
 */

import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import { Dexie, type EntityTable, type Table } from 'dexie'
import { applyEncryptionMiddleware, clearAllTables, cryptoOptions } from 'dexie-encrypted'
import { logger } from '@/utils/logger'
import type { Folder, GComment, GFile, GFileAction, GFileMetadata, GParent, GPerson, GReply, Registry } from '@/types/google'

export const DB_NAME_PREFIX = 'FF_'
export const ENCRYPT_STORAGE_KEY = 'encryptDB'

export function isEncryptionEnabled (): boolean {
    return localStorage.getItem(ENCRYPT_STORAGE_KEY) !== 'false'
}

export type FolderFeedDB = Dexie & {
    folders: EntityTable<Folder, 'id'>
    files: Table<GFile, [number, string]>  // 'folderId' | 'gid'
    comments: Table<GComment, [number, string, string]>  // 'folderId' | 'fileGid' | 'commentId'
    replies: Table<GReply, [number, string, string, string]>  // 'folderId' | 'fileGid' | 'commentId' | 'replyId'
    actions: EntityTable<GFileAction, 'id'>
    people: EntityTable<GPerson, 'personId'>
    tree: EntityTable<GParent, 'gid'>
    fileMetadata: EntityTable<GFileMetadata, 'gid'>
    registry: EntityTable<Registry, 'key'>
}

export const useDatabaseStore = defineStore('database', (): {
    // db: FolderFeedDB | null
    currentUserId: Ref<string | null>
    initializeDatabase: (userId: string, encryptionKeyHex: string) => Promise<FolderFeedDB>
    closeDatabase: () => void
    getDatabase: () => FolderFeedDB
    whenReady: () => Promise<FolderFeedDB>
    failInitialization: (error: unknown) => void
} => {
    // State
    let db: FolderFeedDB | null = null
    const currentUserId = ref<string | null>(null)
    const readyWaiters: Array<{ resolve: (db: FolderFeedDB) => void, reject: (error: unknown) => void }> = []

    /**
     * Initialize the database for a specific user
     */
    async function initializeDatabase (userId: string, encryptionKeyHex: string) {
        // If already initialized for this user, skip
        if (currentUserId.value === userId && db) {
            return db
        }

        // Close existing database if any
        if (db) {
            closeDatabase()
        }

        // Create new database instance
        const dbName = `${DB_NAME_PREFIX}${userId}`
        const newDb = new Dexie(dbName) as FolderFeedDB

        if (isEncryptionEnabled()) {
            // 32 random bytes generated server-side, hex-encoded
            const symmetricKey = Uint8Array.from(
                encryptionKeyHex.match(/../g) ?? [],
                byte => Number.parseInt(byte, 16),
            )

            applyEncryptionMiddleware(newDb, symmetricKey, {
                comments: {
                    type: cryptoOptions.ENCRYPT_LIST,
                    fields: ['data', 'fileTitle'], // Only these fields get encrypted
                },
                replies: {
                    type: cryptoOptions.ENCRYPT_LIST,
                    fields: ['data'], // Only these fields get encrypted
                },
                registry: cryptoOptions.NON_INDEXED_FIELDS,
                people: cryptoOptions.NON_INDEXED_FIELDS,
            }, clearAllTables) // clearEncryptedTables
        }

        // Define the database schema
        newDb.version(1).stores({
            folders: '++id, &gid',   // syncTime, *actors
            files: '&[folderId+gid], folderId, gid, *actors, ts, [folderId+ts]',
            actions: '++id, folderId, [folderId+gid], [folderId+ts]',
            people: '&personId',
            fileMetadata: '&gid',
            tree: '&gid, parentGid',

            // [folderId+fileGid+modifiedTs] is used to efficiently query comments by their last modified time when opening a file
            comments: '&[folderId+fileGid+commentId], folderId, [folderId+fileGid+modifiedTs]',

            replies: '&[folderId+fileGid+commentId+replyId], folderId, [folderId+fileGid+commentId]',
            registry: '&key',
        })
        // TODO: files.[folderId+ts] index is needed when a custom fromTs/toTs range is implemented
        // TODO: files.multi-valued index on actors used for "Person" view

        // newDb.version(2).stores({
        // })

        // cSpell:disable

        /*
        newDb.on('populate', function () {
        })
        */
        /* cSpell:enable */

        // Open the database
        await newDb.open()

        // Update state
        db = newDb
        currentUserId.value = userId

        for (const waiter of readyWaiters.splice(0)) {
            waiter.resolve(newDb)
        }

        // logger.log(`Database initialized for user: ${userId}`)
        return newDb
    }

    /**
     * Resolves once the database is initialized (immediately if it already is)
     */
    function whenReady (): Promise<FolderFeedDB> {
        if (db) {
            return Promise.resolve(db)
        }
        return new Promise((resolve, reject) => {
            readyWaiters.push({ resolve, reject })
        })
    }

    /**
     * Reject everyone waiting in `whenReady()` when initialization fails
     */
    function failInitialization (error: unknown) {
        for (const waiter of readyWaiters.splice(0)) {
            waiter.reject(error)
        }
    }

    /**
     * Close the current database
     */
    function closeDatabase () {
        if (db) {
            db.close()
            db = null
            currentUserId.value = null
            logger.log('Database closed')
        }
    }

    /**
     * Get the current database instance
     * Throws an error if database is not initialized
     */
    function getDatabase (): FolderFeedDB {
        if (!db) {
            throw new Error('Database not initialized. Please log in first.')
        }
        return db
    }

    return {
        // State
        // db,
        currentUserId,
        // Actions
        initializeDatabase,
        closeDatabase,
        getDatabase,
        whenReady,
        failInitialization,
    }
})
